import _ from 'lodash';

import {
    MessagingWebappPageEvent,
    log as logMessagingWebappPageEvent,
} from '@indeed/signals-schema/com/indeed/signals/schema/messaging/MessagingWebappPageEvent';

import {
    ModernMessagingDebugging,
    log as logModernMessagingDebugging,
    AvroOptional,
} from '@indeed/signals-schema/com/indeed/signals/schema/messaging/ModernMessagingDebugging';

import {
    HttpClientResponse,
    IMessagingServiceClient,
    ItemType,
    UserActionLog,
    ImpressionLog,
} from '../types/messaging-types';

import {
    ConversationImpressionLog,
    JobDetailImpressionLog,
    LogLevel,
    ReduxActionLog,
    PageEventType,
} from './types';
import { convertReduxActionLog } from './utils/convertReduxActionLog';

import { convertImpressionLog } from './utils/convertImpressionLog';
import { checkIsTrueOrThrow } from '../server/utils/preconditions';
import { SignalsErrorLoggerConfig, initializeSignalsErrorLogger } from './utils/javascriptLogger';

const GLOBAL_NAV_SENTRY_OBJECT_PATH = 'IndeedSentry.Sentry';
const DEBUG_APP_NAME = 'JS Webapp';

export class BrowserLogger {
    private readonly msgsClient: IMessagingServiceClient;
    private readonly pageLoadTk: string;
    private readonly signalsErrorLogger: any;

    // tslint:disable-next-line: no-any
    private readonly Sentry: any;

    constructor(
        msgsClient: IMessagingServiceClient,
        pageLoadTk: string,
        user: { email: string },
        signalsErrorLoggerConfig: SignalsErrorLoggerConfig
    ) {
        this.msgsClient = msgsClient;
        this.pageLoadTk = pageLoadTk;
        this.Sentry = _.get(window, GLOBAL_NAV_SENTRY_OBJECT_PATH);

        if (this.Sentry) {
            // tslint:disable-next-line: no-any
            this.Sentry.getCurrentHub().configureScope((scope: any) => {
                scope.setUser({ email: user.email });
            });
        }
        if (signalsErrorLoggerConfig) {
            try {
                this.signalsErrorLogger = initializeSignalsErrorLogger(signalsErrorLoggerConfig);
            } catch (e) {
                // @ts-ignore
                const { message, stack } = e;
                const errorMsg = `Error initializing signals error logger: ${message}`;

                const errorToLog = new Error(errorMsg);
                errorToLog.stack = stack;

                this.logException(errorToLog);
            }
        }
    }

    public logCaughtError(e: Error): void {
        if (this.Sentry) {
            this.Sentry.captureException(e);
        }

        if (this.signalsErrorLogger) {
            // line, col are non-standard attributes of Error, and they rarely provide useful info after
            // our source files are bundled and minified
            this.signalsErrorLogger.jsError.logJsErr(
                e.message,
                window.location.href,
                undefined,
                undefined,
                e
            );
        }
    }

    public logPageEvent(pageEventType: PageEventType): void {
        // Log to logrepo via Signals, in msgw_tAction
        // If logged by Signals, you see appinstance ClickstreamLogrepoConsumerDaemon in the log
        const eventToLog: MessagingWebappPageEvent = {
            action: pageEventType,
            appinstance: 'Signals',
            application: 'messaging-webapp',
            parenttk: this.pageLoadTk,
        };
        logMessagingWebappPageEvent(eventToLog);
    }

    // Log error to sentry
    public logException(e: Error): void {
        if (this.Sentry) {
            this.Sentry.captureException(e);
        }
    }

    // Logs an generic string
    public logMessage(level: LogLevel, msg: string): void {
        if (this.Sentry) {
            this.Sentry.captureMessage(msg, level);
        }
    }

    public logReduxAction(log: ReduxActionLog): void {
        // tslint:disable-next-line: no-void-expression
        void this.logUserAction(convertReduxActionLog(log));
    }

    public logConversationImpression(log: ConversationImpressionLog): void {
        void this.logImpression(convertImpressionLog(log, ItemType.CONVERSATIONS));
    }

    public logJobDetailImpression(log: JobDetailImpressionLog): void {
        void this.logImpression(convertImpressionLog(log, ItemType.JOB_DETAIL));
    }

    public logButtonImpression(buttonNames: string[]): void {
        void this.logImpression({
            itemType: ItemType.BUTTON,
            itemUids: buttonNames,
            itemsAttributes: {},
        });
    }

    public logUserAction(log: UserActionLog): Promise<HttpClientResponse> {
        return this.msgsClient.logging.v1.postUserActionLog(this.pageLoadTk, log);
    }

    public async logImpression(log: ImpressionLog): Promise<HttpClientResponse> {
        BrowserLogger.validateImpressionLog(log);

        return this.msgsClient.logging.v1.postImpressionLog(this.pageLoadTk, log); //  v1.postImpressionLog(impressionLog);
    }

    public static validateImpressionLog(log: ImpressionLog) {
        checkIsTrueOrThrow(_.isArray(log.itemUids), `item uids is not array: ${log.itemUids}`);

        const size = log.itemUids.length;
        checkIsTrueOrThrow(size > 0, 'item uids are empty');

        Object.entries(log.itemsAttributes).forEach(([attributeName, attributeValue]) => {
            checkIsTrueOrThrow(
                _.isArray(attributeValue),
                `${attributeName} is not array: ${attributeValue}`
            );
            checkIsTrueOrThrow(
                attributeValue.length === size,
                `${attributeName} length: ${attributeValue.length} doesn't match itemUids length: ${size}`
            );
        });
    }

    public logSignalsDebug(
        logMessage: string,
        proctorGroupsString: string = '',
        logDetails?: string,
        accountKey?: string,
        conversationId?: string,
        jobId?: string
    ): void {
        try {
            const proctorGroups = proctorGroupsString.split(',');

            const eventToLog: ModernMessagingDebugging = {
                timestamp: new Date().getTime(),
                appName: DEBUG_APP_NAME,
                logMessage: logMessage,
                logDetails: AvroOptional.string(logDetails),
                accountKey: AvroOptional.string(accountKey),
                conversationId: AvroOptional.string(conversationId),
                jobId: AvroOptional.string(jobId),
                proctorGroups: AvroOptional.array(proctorGroups),
            };
            logModernMessagingDebugging(eventToLog);
        } catch (e) {
            this.logCaughtError(e as Error);
        }
    }
}
